import { PlacementWithLocation, Shape } from "../../../../types";
import { findSpotForPosition } from "../../TemporalTest/positionMapping";
import { GroupedTrip } from "../types";

/**
 * Groups placement segments by trip_id and calculates combined time ranges.
 *
 * This is used to consolidate multiple placement segments for the same trip
 * into a single display item, avoiding confusion when trips have multiple
 * segments (e.g., when an aircraft moves between spots during its stay).
 *
 * @param placements Array of placement segments from the database
 * @param shapes Optional array of ramp shapes (spots, zones) for spot name lookup
 * @param rampDepth Optional ramp depth for coordinate transformation
 * @returns Array of grouped trips with consolidated time ranges
 */
export function groupPlacementsByTrip(
  placements: PlacementWithLocation[],
  shapes?: Shape[],
  rampDepth?: number
): GroupedTrip[] {
  // Group placements by trip_id
  const tripMap = new Map<string, PlacementWithLocation[]>();

  placements.forEach((placement) => {
    const tripId = placement.trip_id;
    if (!tripMap.has(tripId)) {
      tripMap.set(tripId, []);
    }
    tripMap.get(tripId)!.push(placement);
  });

  // Convert to GroupedTrip array
  const groupedTrips: GroupedTrip[] = [];

  tripMap.forEach((segments, tripId) => {
    // Sort segments by start time
    const sortedSegments = segments.sort(
      (a, b) =>
        new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );

    // Use first segment for aircraft details (all segments should have same details)
    const firstSegment = sortedSegments[0];

    // Find earliest start and latest end across all segments
    const startTime = sortedSegments.reduce((earliest, seg) => {
      const segStart = new Date(seg.start_time);
      return segStart < new Date(earliest) ? seg.start_time : earliest;
    }, sortedSegments[0].start_time);

    const endTime = sortedSegments.reduce((latest, seg) => {
      const segEnd = new Date(seg.end_time);
      return segEnd > new Date(latest) ? seg.end_time : latest;
    }, sortedSegments[0].end_time);

    // Calculate current spot name from first segment's position
    let current_spot_name: string | undefined;
    if (shapes && rampDepth != null && firstSegment.x != null && firstSegment.y != null) {
      const spotId = findSpotForPosition(
        { x: firstSegment.x, y: firstSegment.y },
        shapes,
        rampDepth
      );
      if (spotId) {
        const spotShape = shapes.find((s) => s.id === spotId && s.type === "Spot");
        current_spot_name = spotShape?.text || spotId;
      }
    }

    groupedTrips.push({
      trip_id: tripId,
      tail_number: firstSegment.tail_number,
      aircraft_model: firstSegment.aircraft_model,
      aircraft_make: firstSegment.aircraft_make,
      start_time: startTime,
      end_time: endTime,
      segment_count: segments.length,
      placement_ids: segments.map((s) => s.id),
      wingspan: (firstSegment as any).wingspan, // Added for size badge display
      placement_source: firstSegment.placement_source, // For color indicators
      is_locked: firstSegment.is_locked, // For lock status display
      current_spot_name, // Current spot assignment
    });
  });

  // Sort by start time for consistent display
  return groupedTrips.sort(
    (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
  );
}
